import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import React, { useMemo } from 'react'
import { IconNames } from '@blueprintjs/icons'
import { Icon } from '@blueprintjs/core'

import 'browser/mobile/styles/_control.scss'
import './_barcode-field.scss'

import LabelFieldFactory from 'browser/components/atomic-elements/higher-order-components/label-field-factory'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { getDebugId } from 'browser/app/utils/utils'
import { translateString } from 'shared-libs/helpers/utils'
import { MobileInputField } from '../input/mobile-input-field'
import { Barcode, IBarcodeProps } from 'browser/components/atomic-elements/atoms/barcode/barcode'
import { MobileModal } from 'browser/mobile/elements/modal/modal'
import { Entity } from 'shared-libs/models/entity'
import { Image } from 'shared-libs/generated/server-types/entity/fileSet'
import { ImageLoader } from 'browser/components/atomic-elements/atoms/image-loader/image-loader'
import { ImageEditorModal } from 'browser/components/atomic-elements/organisms/image-editor-modal'
import { withContext } from 'shared-libs/components/context/with-context'
import { ComponentsContext, IComponentsContext } from 'browser/contexts/components/components-context'
import { IImageEditorCarouselProps } from 'browser/components/atomic-elements/organisms/image-editor-carousel/image-editor-carousel'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'
import { BarcodeScanner } from './barcode-scanner-modal'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'

/**
 * @uiComponent
 */
interface IMobileBarcodeFieldProps extends IBaseProps, IBarcodeProps {
  frames?: FramesManager
  value?: string
  entity: Entity
  emptyButtonText?: string
  helpText?: string
  carouselProps?: Partial<IImageEditorCarouselProps>
  onChange: (value: any, silentUpdate?: boolean, overridePath?: string) => void
  imagePath?: string
  /**
   * The operation mode for the barcode component. Defaults to "capture".
   */
  mode?: 'capture' | 'display'
  /**
   * CSS properties for the container that wraps the barcode canvas.
   */
  containerStyle?: React.CSSProperties
}

interface ContextProps {
  componentsContext?: IComponentsContext
}

@withContext(ComponentsContext, 'componentsContext')
class MobileBarcodeField extends React.Component<IMobileBarcodeFieldProps & ContextProps> {
  static defaultProps: Partial<IMobileBarcodeFieldProps> = {
    mode: 'capture',
    carouselProps: {},
  }

  constructor(props: IMobileBarcodeFieldProps) {
    super(props)

    this.state = {}
  }

  render() {
    const { mode } = this.props
    return mode === 'display' ? this.renderDisplay() : this.renderInput()
  }

  private renderDisplay() {
    const { frames, containerStyle } = this.props
    const debugId = getDebugId(frames)

    // https://withvector.atlassian.net/browse/VD-19026
    // Samsung's stock browser on Android forces dark mode, so when rendering
    // the barcode we'll force a light SVG that it can't alter.
    const bgColor = 'white'
    const bgStyle = {
      backgroundImage: `url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20100%20100%22%3E%3Crect%20width%3D%22100%22%20height%3D%22100%22%20fill%3D%22${bgColor}%22%2F%3E%3C%2Fsvg%3E")`,
      ...containerStyle,
    }

    return <Barcode {...this.props} containerStyle={bgStyle} data-debug-id={debugId} />
  }

  private renderInput() {
    const path = this.normalizeImagePath()
    const image = path ? this.props.entity.get(path)?.[0] : undefined

    return (
      <BarcodeField
        {...this.props}
        onInputValue={this.onCapture}
        onInputClick={this.handleOpenScanner}
        onThumbnailClick={this.handleOpenImagePreview}
        image={image}
      />
    )
  }

  private onCapture = (barcode: string) => {
    const { onChange } = this.props
    onChange?.(barcode)
  }

  private onCaptureImage = (barcode: string, blob: Blob, width: number, height: number) => {
    const { entity, onChange } = this.props

    const path = this.normalizeImagePath()
    if (!path) {
      return
    }

    // create File with safe filename
    const filename = _.chain(barcode)
      .deburr()
      .trim()
      .replace(/[^a-zA-Z0-9_.-]/g, '_')
      .truncate({ length: 255, omission: '' })
      .value()
    const file = new File([blob], `${filename}.png`, { type: 'image/png' })

    const uniqueId = uuidv4()
    entity.clearMultipartFiles(path)
    entity.addMultipartFiles(path, [{ file, uniqueId }])

    const uri = URL.createObjectURL(file)

    const fileJson = {
      name: file.name,
      type: 'image',
      uniqueId,
      uri,
      source: [
        {
          width: width,
          height: height,
          uri,
        },
      ],
      width: width,
      height: height,
      isEditable: false,
      transformations: [],
      transformUri: uri,
    }

    const files = [fileJson]

    onChange?.(files, false, path.join('.'))
  }

  /** normalize the images path to better handle arrays */
  private normalizeImagePath(): string[] | undefined {
    const { frames, imagePath } = this.props
    if (!imagePath) {
      return
    }

    const valuePath: string[] = frames?.getContext('valuePath')

    // need to find the parent path
    const normalizedImagePath = _.split(imagePath, '.')
    const parentIndex = _.findLastIndex(valuePath, (value) => typeof value === 'number')

    return !valuePath || parentIndex === -1
      ? normalizedImagePath
      : [..._.slice(valuePath, 0, parentIndex + 1), ...normalizedImagePath]
  }

  private handleOpenScanner = () => {
    const { imagePath } = this.props
    const onCaptureImage = imagePath ? this.onCaptureImage : undefined

    MobileModal.open(
      <BarcodeScanner
        {...this.props}
        onCapture={this.onCapture}
        onCaptureImage={onCaptureImage}
      />
    )
  }

  private handleOpenImagePreview = () => {
    const { entity, imagePath, carouselProps } = this.props

    const { schema } = entity.resolveSubschemaByValuePath(imagePath)

    const componentsContext = {
      ...this.props.componentsContext,
      platform: this.props.componentsContext.platform,
    }

    ImageEditorModal.open(
      {
        ...carouselProps,
        entity,
        schema,
        imagesPath: imagePath,
        index: 0,
        isEditable: true,
        editControls: <this.renderEditControls />,
        onClose: () => this.forceUpdate(),
      },
      componentsContext
    )
  }

  private renderEditControls = ({ onClose }: { onClose?: () => void }) => {
    const handleImageDelete = () => {
      const { entity, imagePath, onChange } = this.props

      ConfirmationModal.open({
        confirmationText: 'Do you want to delete this image?',
        confirmationTitle: 'Review Changes',
        modalDialogClassName: 'c-modal-dialog--sm',
        onPrimaryClicked: () => {
          entity.clearMultipartFiles(imagePath)
          onChange?.(undefined, false, imagePath)
          onClose?.()
        },
        primaryButtonText: 'Confirm',
      })
    }

    return (
      <div className="u-flex u-bumperRight--xs">
        <div className="c-cardHeader-item c-cardHeader-item--smallMargin">
          <Button onClick={handleImageDelete}>
            <Icon icon={IconNames.TRASH} />
          </Button>
        </div>
      </div>
    )
  }
}

const LabeledMobileBarcodeField = LabelFieldFactory({ InputComponent: MobileBarcodeField })
export { LabeledMobileBarcodeField as MobileBarcodeField }

interface IBarcodeFieldProps extends IMobileBarcodeFieldProps {
  onInputValue: (barcode: string) => void
  onInputClick: () => void
  onThumbnailClick: () => void
  image: Image | undefined
}

const BarcodeField: React.FC<IBarcodeFieldProps> = (props) => {
  const { frames, value, emptyButtonText, onInputValue, onInputClick, onThumbnailClick } = props
  const debugId = getDebugId(frames)

  const translationTable = _.merge({}, globalTranslationTable, frames?.getContext('translationTable'))
  const translatedEmptyButtonText = useMemo(
    () => translateString(emptyButtonText || 'Barcode_Field_Press_to_Scan', translationTable),
    [emptyButtonText]
  )

  const showImage = !!props.image
  const onImageClick = showImage ? onThumbnailClick : undefined

  if (_.isEmpty(value)) {
    return (
      <Button
        className="w-100 mv2 mobile-button flex flex-column"
        onClick={props.onInputClick}
        data-debug-id={debugId}
      >
        <div className="w-100 p-100 pa0 ma0 flex flex-column tc">
          <div>
            <Icon icon="barcode" size={24} />
          </div>
          <div>{translatedEmptyButtonText}</div>
        </div>
      </Button>
    )
  }
  return (
    <div className="mobile-media-input-container w-100">
      <div className="mobile-media-input-field">
        <MobileInputField
          inputClassName="mobile-control"
          frames={frames}
          value={props.value}
          onChange={onInputValue}
          data-debug-id={debugId}
        />
      </div>
      <span
        style={{ maxWidth: '15%' }}
        className="mobile-media-input-thumbnail"
        onClick={showImage ? onImageClick : onInputClick}
      >
        {!showImage && (
          <Icon
            className="mobile-icon content-center"
            style={{ justifyItems: 'center' }}
            color="#f89939"
            icon="barcode"
          />
        )}
        {showImage && <Thumbnail image={props.image} />}
      </span>
    </div>
  )
}

const Thumbnail = ({ image }: { image: Image }) => {
  return (
    <ImageLoader
      className="c-thumbnail c-thumbnail--image"
      imageClassName="c-image"
      style={{ maxHeight: '44pt' }}
      src={image.uri}
    />
  )
}


